import { useCallback, useEffect, useRef, useState } from "react";
import {
  completeAsmLoginCallback,
  consumeAsmCompletedMarker,
  endAsmAgentServerSession,
  isAsmCallbackPending,
  refreshAsmAccessToken,
  revokeAsmRefreshToken,
  startAsmLogin,
} from "~/features/assistedService/api/asmAuth";
import {
  clearAsmSession,
  notifyAsmSessionChanged,
  setAsmEmulatedUser,
  useAsmSessionSnapshot,
} from "~/features/assistedService/api/asmSession";
import { postAsmSessionEventEnd, postAsmSessionEventStart } from "~/features/assistedService/api/asmAswsApi";
import { clearConfiguratorResumeStores } from "~/domains/configurator/configuratorResumeStore";
import { advanceResumeAuthEpoch } from "~/domains/configurator/configuratorResumeAuthEpoch";
import type { PersonifyRequest } from "~/features/assistedService/types";
import { readAsmCollapseCookie, writeAsmCollapseCookie } from "~/features/assistedService/utils/asmCollapseCookie";
import { stripAsmAgentParam } from "~/features/assistedService/utils/asmUrl";
import { clearPersistedAccountContext, persistAccountContext } from "~/domains/user/selectedAccountPersistence";
import { broadcastAuthChanged } from "~/features/auth/login/api/authTabChannel";

interface Alert {
  messageCode: string;
  alertClass: string | null;
}

interface UseAssistedServiceResult {
  state: ReturnType<typeof useAsmSessionSnapshot>;
  remainingSeconds: number;
  collapsed: boolean;
  alert: Alert | null;
  isLoading: boolean;
  loginPending: boolean;
  personifyPending: boolean;
  login: () => void;
  personify: (request: PersonifyRequest) => Promise<void>;
  personifyStop: () => Promise<void>;
  logout: () => Promise<void>;
  closeAssistedService: () => Promise<void>;
  resetTimer: () => Promise<void>;
  toggleCollapsed: () => void;
}

const extractError = (error: unknown): Alert => {
  const message = (error as { message?: string } | null)?.message;
  return { messageCode: typeof message === "string" && message ? message : "asm.error", alertClass: "" };
};

/** Best-effort audit END for an emulated customer; never blocks the identity change. */
const endEmulationAudit = async (customerUid: string | undefined): Promise<void> => {
  if (!customerUid) {
    return;
  }
  await postAsmSessionEventEnd(customerUid).catch((error: unknown) => {
    console.warn("ASM: session END audit event failed.", error);
  });
};

/**
 * Seeds the emulated customer's account context for the next document load
 * (the SCHAEFF-41427 persisted tuple): the cold-load OCC session request
 * carries soldTo/salesOrg and the backend authorizes it for the customer. A
 * suggestion without an account context falls back to the customer's default.
 */
const persistEmulatedAccountContext = (request: PersonifyRequest): void => {
  const siteUid = window.__SCHA_BOOT__?.siteUid;
  if (siteUid && request.accountId && request.salesOrg) {
    persistAccountContext({
      siteUid,
      userUid: request.customerId,
      soldTo: request.accountId,
      salesOrg: request.salesOrg,
    });
  } else {
    clearPersistedAccountContext();
  }
};

/**
 * Orchestrates the ASM bar on the token-state model (task 0250 lane 2a):
 * the session snapshot comes from {@link useAsmSessionSnapshot} (agent JWT +
 * FE emulated-user context), the countdown is the token expiry, login is the
 * PKCE authorization_code redirect (`schaeffler-asm`), logout is the F50
 * revocation pair, personify clears/sets FE context plus the ASWS audit
 * events, and the reset button silently renews via the rotating refresh
 * token (replaces the retired `/resetSession` + `/refresh` routes).
 */
export function useAssistedService(): UseAssistedServiceResult {
  const state = useAsmSessionSnapshot();
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [alert, setAlert] = useState<Alert | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [loginPending, setLoginPending] = useState<boolean>(false);
  const [personifyPending, setPersonifyPending] = useState<boolean>(false);

  // Complete an ASM PKCE callback before anything else: the completion hard-navigates
  // back to the return URL, so the bar must not act on a half-state.
  useEffect(() => {
    if (isAsmCallbackPending()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- MOUNT-ONLY: one-time flag while the pending PKCE callback completes; the completion hard-navigates away, so no re-render cascade can follow
      setLoginPending(true);
      void completeAsmLoginCallback();
      return;
    }
    if (consumeAsmCompletedMarker()) {
      clearPersistedAccountContext();
      setAsmEmulatedUser(null);
      broadcastAuthChanged("asm");
      notifyAsmSessionChanged();
    }
  }, []);

  // Restore the collapse cookie once on mount.
  useEffect(() => {
    const stored = readAsmCollapseCookie();
    if (stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- MOUNT-ONLY: one-time restore of the external collapse cookie into state on mount; render-derivation would re-read the cookie every render
      setCollapsed(Boolean(stored.state));
    }
  }, []);

  // Re-sync the countdown to the token expiry whenever the session changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- SYNC-EXTERNAL: local countdown mirrors the token expiry snapshot; also decremented by the interval and reset by resetTimer, so it cannot be render-derived
    setRemainingSeconds(state.agent ? Math.max(0, Math.ceil((state.expiresAt - Date.now()) / 1000)) : 0);
  }, [state.agent, state.expiresAt]);

  // Agent-session countdown; fires the F50 logout on expiry. Only runs while an agent is logged in.
  const doLogoutRef = useRef<() => Promise<void>>(async () => {});
  const remainingSecondsRef = useRef(remainingSeconds);
  useEffect(() => {
    remainingSecondsRef.current = remainingSeconds;
  }, [remainingSeconds]);
  const expired = remainingSeconds <= 0;
  useEffect(() => {
    if (!state.agent || expired) {
      return;
    }
    const interval = window.setInterval(() => {
      const next = Math.max(remainingSecondsRef.current - 1, 0);
      setRemainingSeconds(next);
      if (next === 0) {
        window.clearInterval(interval);
        void doLogoutRef.current();
      }
    }, 1000);
    return () => window.clearInterval(interval);
  }, [state.agent, expired]);

  const doLogout = useCallback(async () => {
    // F50: logout/revocation is the only session terminator (replaces both the
    // retired `/logoutasm` and `/quit` semantics). The revocation pair is
    // best-effort; the local context is always cleared. A running emulation
    // is audited as ended first, while the agent token is still usable.
    const emulatedUid = state.emulatedUser?.uid;
    await endEmulationAudit(emulatedUid);
    await Promise.allSettled([revokeAsmRefreshToken(), endAsmAgentServerSession()]);
    // ASM identity boundary (FE review round 2): the agent session ends with
    // possession-based pointers still in the browser — the epoch advance plus
    // the domains-level clear run BEFORE clearAsmSession() drops the FE
    // identity, so no in-flight pointer write can re-file a pointer into the
    // logged-out tab (features may not import the app layer that owns the
    // browser adapters).
    advanceResumeAuthEpoch();
    clearConfiguratorResumeStores();
    clearPersistedAccountContext();
    clearAsmSession();
    broadcastAuthChanged("asm");
    window.location.reload();
  }, [state.emulatedUser?.uid]);
  useEffect(() => {
    doLogoutRef.current = doLogout;
  }, [doLogout]);

  const login = useCallback(() => {
    setLoginPending(true);
    // Navigates to the AS login page; the callback completion resets the flag.
    void startAsmLogin();
  }, []);

  const personify = useCallback(async (request: PersonifyRequest) => {
    setPersonifyPending(true);
    try {
      // Audit START via ASWS with the agent token (0246 route map, route 4);
      // the FE context makes the emulating state visible without a server state call.
      await postAsmSessionEventStart(request.customerId);
      // ASM identity boundary (FE review round 2): the emulated customer's
      // context begins here — the previous context's resume pointers die
      // (settled decision 13) and the epoch advance drops every in-flight
      // pointer write BEFORE the emulated identity takes effect.
      advanceResumeAuthEpoch();
      clearConfiguratorResumeStores();
      persistEmulatedAccountContext(request);
      setAsmEmulatedUser({ uid: request.customerId, name: request.customerName ?? request.customerId });
      broadcastAuthChanged("asm");
      // From here every OCC request acts for the customer (occFetch emulation
      // channel); reload so no state of the previous identity survives.
      window.location.reload();
    } catch (error) {
      setAlert(extractError(error));
      setPersonifyPending(false);
    }
  }, []);

  const personifyStop = useCallback(async () => {
    // Clear the FE context (the bar leaves the emulating state even if the
    // audit call degrades), then the best-effort END event (task 0251 route).
    const emulated = state.emulatedUser;
    // ASM identity boundary (FE review round 2): stopping the emulation ends
    // the emulated customer's session for this browser — the epoch advance
    // plus the pointer clear run BEFORE setAsmEmulatedUser(null) so their
    // possession-based pointers neither leak into the next personified
    // customer's anonymous context nor get re-filed by an in-flight write.
    advanceResumeAuthEpoch();
    clearConfiguratorResumeStores();
    clearPersistedAccountContext();
    setAsmEmulatedUser(null);
    broadcastAuthChanged("asm");
    await endEmulationAudit(emulated?.uid);
    // Back to the agent's anonymous storefront: drop the customer's state.
    window.location.reload();
  }, [state.emulatedUser]);

  const closeAssistedService = useCallback(async () => {
    // Close = the same F50 terminator, but the post-logout URL drops the
    // launch marker so the bar does not remount (legacy close contract).
    const emulatedUid = state.emulatedUser?.uid;
    await endEmulationAudit(emulatedUid);
    await Promise.allSettled([revokeAsmRefreshToken(), endAsmAgentServerSession()]);
    // ASM identity boundary (FE review round 2): close terminates the agent
    // session too — the same epoch advance plus pointer clear as doLogout,
    // BEFORE the FE session state drops.
    advanceResumeAuthEpoch();
    clearConfiguratorResumeStores();
    clearPersistedAccountContext();
    clearAsmSession();
    broadcastAuthChanged("asm");
    window.location.replace(stripAsmAgentParam(window.location.href));
  }, [state.emulatedUser?.uid]);

  const resetTimer = useCallback(async () => {
    // Replaces the retired `/resetSession`: silently renew via the rotating
    // refresh token; the countdown restarts from the new expiry.
    try {
      const renewed = await refreshAsmAccessToken();
      if (!renewed) {
        setAlert({ messageCode: "asm.error", alertClass: "" });
        return;
      }
      notifyAsmSessionChanged();
      setRemainingSeconds(Math.max(0, Math.ceil((renewed.expiresAt - Date.now()) / 1000)));
    } catch (error) {
      setAlert(extractError(error));
    }
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((previous) => {
      const next = !previous;
      writeAsmCollapseCookie({ position: 0, state: next });
      return next;
    });
  }, []);

  // Alert auto-fade after 3s (matches legacy behaviour).
  useEffect(() => {
    if (!alert) {
      return;
    }
    const timeout = window.setTimeout(() => setAlert(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [alert]);

  return {
    state,
    remainingSeconds,
    collapsed,
    alert,
    // Loading = an ASM PKCE callback is being completed (hard navigation follows).
    isLoading: isAsmCallbackPending(),
    loginPending,
    personifyPending,
    login,
    personify,
    personifyStop,
    logout: () => doLogout(),
    closeAssistedService,
    resetTimer,
    toggleCollapsed,
  };
}
